<script setup lang="ts">
import converters from "@/converters";
import { type IFileState, type IValidationResult } from "@/interfaces";
import FilePreview from "@/stories/displays/FileGallery/FilePreview.vue";
import MediaPreviewDialog from "@/stories/displays/MediaPreviewDialog/MediaPreviewDialog.vue";
import { computed, ref, toRefs } from "vue";

interface Props {
  name?: string;
  files: IFileState[];
  preview?: boolean;
  favorite?: boolean;
  deletable?: boolean;
  nameEditing?: boolean;
  validation?: IValidationResult | null;
  showFavoriteIcon?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  preview: false,
  favorite: false,
  deletable: true,
  nameEditing: false,
  showFavoriteIcon: true,
});

const { files } = toRefs(props);

const emit = defineEmits<{
  click: [IFileState];
  delete: [IFileState];
  favorite: [IFileState];
  edit: [IFileState];
}>();

const previewIndex = ref<number | null>(null);

const filteredFiles = computed(() => {
  return files.value.filter((item) => item.action !== "delete");
});

const handleClick = (file: IFileState, index: number) => {
  previewIndex.value = index;
  emit("click", file);
};

const medias = computed(() => {
  return filteredFiles.value.map((file) =>
    converters.medias.toMedia(file, "image"),
  );
});
</script>

<template>
  <div class="flex gap-4 w-full h-auto flex-wrap">
    <FilePreview
      type="button"
      v-for="(file, index) of filteredFiles"
      :key="index"
      :file="file"
      :preview="preview"
      :favorite="favorite"
      :deletable="deletable"
      :nameEditing="nameEditing"
      :validation="validation"
      :showFavoriteIcon="showFavoriteIcon"
      :text-input-name="`${name || 'file'}.${index}.name`"
      @click="handleClick(file, index)"
      @delete="$emit('delete', file)"
      @favorite="$emit('favorite', file)"
      @edit="$emit('edit', file)"
    />
    <MediaPreviewDialog
      v-if="previewIndex !== null"
      :medias="medias"
      :defaultIndex="previewIndex"
      @close="previewIndex = null"
    />
  </div>
</template>
